import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useWallet } from '@/context/WalletContext';
import { toast } from 'sonner';
import {
  Coins,
  ArrowDownUp,
  Loader2,
  RefreshCw,
  CheckCircle,
  ExternalLink,
  AlertCircle,
  ArrowRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// STXOSHI contract - used for sell swaps
const STXOSHI_CONTRACT = "SP1Z92MPDQEWZXW36VX71Q25HKF5K2EPCJ304F275.tokensoft-token-v4karmaedcr";

// Detect if on mobile device
const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth <= 768;
};

export default function SwapSTXOSHIButton({ className = "", size = "lg", defaultMode = "buy", trigger = null }) {
  const { isConnected, addresses, signStxTransfer, signContractCall, connect } = useWallet();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(defaultMode); // "buy" or "sell"
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [sellPriceData, setSellPriceData] = useState(null);
  const [quote, setQuote] = useState(null);
  const [swapStatus, setSwapStatus] = useState('idle');
  const [completedSwap, setCompletedSwap] = useState(null);

  // Fetch current prices
  const fetchPrice = useCallback(async () => {
    try {
      setPriceLoading(true);
      const [buyRes, sellRes] = await Promise.all([
        fetch(`${API}/api/swap/price`),
        fetch(`${API}/api/swap/sell/price`)
      ]);
      
      const buyData = await buyRes.json();
      const sellData = await sellRes.json();
      
      if (buyData.success) setPriceData(buyData);
      if (sellData.success) setSellPriceData(sellData);
    } catch (error) {
      console.error('Price fetch error:', error);
    } finally {
      setPriceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchPrice();
      const interval = setInterval(fetchPrice, 30000);
      return () => clearInterval(interval);
    }
  }, [open, fetchPrice]);

  // Get quote when amount changes
  useEffect(() => {
    const getQuote = async () => {
      const numAmount = parseFloat(amount);
      if (!amount || numAmount <= 0) {
        setQuote(null);
        return;
      }
      
      // Validate minimum
      if (mode === 'buy' && numAmount < 1) {
        setQuote(null);
        return;
      }
      if (mode === 'sell' && numAmount < 1000) {
        setQuote(null);
        return;
      }
      
      try {
        const endpoint = mode === 'buy' 
          ? `${API}/api/swap/quote?stx_amount=${numAmount}`
          : `${API}/api/swap/sell/quote?stxoshi_amount=${numAmount}`;
        
        const response = await fetch(endpoint, { method: 'POST' });
        const data = await response.json();
        
        if (data.success) {
          setQuote(data);
        } else if (data.detail) {
          toast.error(data.detail);
          setQuote(null);
        }
      } catch (error) {
        console.error('Quote error:', error);
      }
    };

    const debounce = setTimeout(getQuote, 500);
    return () => clearTimeout(debounce);
  }, [amount, mode]);

  // Reset when mode changes
  useEffect(() => {
    setAmount('');
    setQuote(null);
    setSwapStatus('idle');
    setCompletedSwap(null);
  }, [mode]);

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) {
      setAmount('');
      setQuote(null);
      setSwapStatus('idle');
      setCompletedSwap(null);
    }
  };

  // Execute BUY swap (STX -> STXOSHI)
  const executeBuySwap = async () => {
    if (!quote || !addresses?.stx) return;
    
    setLoading(true);
    setSwapStatus('signing');
    
    try {
      // Desktop: Redirect to ALEX DEX to avoid stx_transferStx network mismatch
      if (!isMobile()) {
        const alexSwapUrl = `https://app.alexlab.co/swap?from=token-wstx&to=token-ssl-STXOSHI-RNTJY&fromAmount=${amount}`;
        
        toast.info('Opening ALEX DEX for swap...', {
          description: 'Desktop swaps use ALEX DEX to avoid wallet issues',
          duration: 5000
        });
        
        window.open(alexSwapUrl, '_blank');
        setLoading(false);
        setSwapStatus('idle');
        setOpen(false);  // Close the dialog
        return;
      }
      
      // Mobile: Use direct STX transfer (works on iPhone Xverse)
      // Initiate swap
      const initRes = await fetch(
        `${API}/api/swap/initiate?stx_amount=${amount}&user_address=${addresses.stx}`,
        { method: 'POST' }
      );
      const initData = await initRes.json();
      
      if (!initData.success) {
        throw new Error(initData.detail || 'Failed to initiate swap');
      }
      
      const stxoshiAmount = quote.output.amount;
      const treasuryWallet = initData.transaction.recipient;  // Use wallet from API response
      
      // Sign STX transfer to treasury (hot wallet)
      const txResult = await signStxTransfer({
        recipient: treasuryWallet,
        amount: Math.floor(parseFloat(amount) * 1_000_000),
        memo: `STXOSHI Swap ${initData.swap_id.slice(0, 8)}`
      });
      
      if (!txResult?.txid) {
        throw new Error('Transaction cancelled or failed');
      }
      
      // Confirm swap
      setSwapStatus('confirming');
      const confirmRes = await fetch(
        `${API}/api/swap/confirm?swap_id=${initData.swap_id}&txid=${txResult.txid}`,
        { method: 'POST' }
      );
      const confirmData = await confirmRes.json();
      
      if (confirmData.success) {
        setSwapStatus('complete');
        const wasAutoFulfilled = confirmData.status === 'fulfilled' && confirmData.payout_txid;
        
        setCompletedSwap({
          txId: confirmData.payout_txid || txResult.txid,
          stxAmount: parseFloat(amount),
          stxoshiAmount,
          autoFulfilled: wasAutoFulfilled,
        });
        
        if (wasAutoFulfilled) {
          toast.success('Swap successful!', {
            description: `Your ${stxoshiAmount.toLocaleString()} STXOSHI has been sent!`,
            duration: 10000,
          });
        } else {
          toast.success('Swap received!', {
            description: `Your ${stxoshiAmount.toLocaleString()} STXOSHI will be sent shortly. Check back in a few minutes.`,
            duration: 10000,
          });
        }
      } else {
        throw new Error(confirmData.detail || 'Swap confirmation failed');
      }
    } catch (error) {
      console.error('Swap error:', error);
      toast.error('Swap failed', { description: error.message });
      setSwapStatus('idle');
    } finally {
      setLoading(false);
    }
  };

  // Execute SELL swap (STXOSHI -> STX)
  const executeSellSwap = async () => {
    if (!quote || !addresses?.stx) return;
    
    setLoading(true);
    setSwapStatus('signing');
    
    try {
      // Initiate sell swap
      const initRes = await fetch(
        `${API}/api/swap/sell/initiate?stxoshi_amount=${amount}&user_address=${addresses.stx}`,
        { method: 'POST' }
      );
      const initData = await initRes.json();
      
      if (!initData.success) {
        throw new Error(initData.detail || 'Failed to initiate swap');
      }
      
      const stxAmount = quote.output.amount;
      
      // Sign STXOSHI token transfer to hot wallet
      const { request } = await import('sats-connect');
      const { cvToHex, uintCV, principalCV, noneCV, postConditionToHex, Pc } = await import('@stacks/transactions');
      
      const amountMicro = Math.floor(parseFloat(amount) * 1_000_000);
      
      // Build post-condition
      const postCondition = Pc.principal(addresses.stx)
        .willSendEq(BigInt(amountMicro))
        .ft(STXOSHI_CONTRACT, 'tokensoft-token');
      
      const payload = {
        contract: STXOSHI_CONTRACT,
        functionName: 'transfer',
        arguments: [
          cvToHex(uintCV(amountMicro)),
          cvToHex(principalCV(addresses.stx)),
          cvToHex(principalCV(initData.hot_wallet)),
          cvToHex(noneCV())
        ],
        postConditions: [postConditionToHex(postCondition)],
        postConditionMode: 'deny'
      };
      
      const response = await request('stx_callContract', payload);
      
      if (!response?.result?.txid) {
        throw new Error('Transaction cancelled or failed');
      }
      
      const txId = response.result.txid;
      
      // Confirm swap
      setSwapStatus('confirming');
      const confirmRes = await fetch(
        `${API}/api/swap/sell/confirm?swap_id=${initData.swap_id}&txid=${txId}`,
        { method: 'POST' }
      );
      const confirmData = await confirmRes.json();
      
      if (confirmData.success) {
        setSwapStatus('complete');
        const wasAutoFulfilled = confirmData.status === 'fulfilled' && confirmData.payout_txid;
        
        setCompletedSwap({
          txId: confirmData.payout_txid || txId,
          stxoshiAmount: parseFloat(amount),
          stxAmount,
          autoFulfilled: wasAutoFulfilled,
        });
        
        if (wasAutoFulfilled) {
          toast.success('Swap successful!', {
            description: `Your ${stxAmount.toFixed(6)} STX has been sent!`,
            duration: 10000,
          });
        } else {
          toast.success('Swap received!', {
            description: `Your ${stxAmount.toFixed(6)} STX will be sent shortly. Check back in a few minutes.`,
            duration: 10000,
          });
        }
      } else {
        throw new Error(confirmData.detail || 'Swap confirmation failed');
      }
    } catch (error) {
      console.error('Sell swap error:', error);
      toast.error('Swap failed', { description: error.message });
      setSwapStatus('idle');
    } finally {
      setLoading(false);
    }
  };

  const handleSwap = () => {
    if (mode === 'buy') {
      executeBuySwap();
    } else {
      executeSellSwap();
    }
  };

  const currentPriceData = mode === 'buy' ? priceData : sellPriceData;
  const inputToken = mode === 'buy' ? 'STX' : 'STXOSHI';
  const outputToken = mode === 'buy' ? 'STXOSHI' : 'STX';
  const minAmount = mode === 'buy' ? 1 : 1000;
  const maxAmount = mode === 'buy' ? 10000 : 100000000;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || <Button 
          size={size}
          className={`bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-bold ${className}`}
          data-testid="swap-stxoshi-btn"
        >
          <Coins className="w-5 h-5 mr-2" />
          Swap STXOSHI
        </Button>}
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ArrowDownUp className="w-5 h-5 text-amber-400" />
            Swap STXOSHI
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Wallet Connection Reminder - small text */}
          {!isConnected && (
            <p className="text-xs text-amber-400/80 text-center">
              Connect wallet in header to swap
            </p>
          )}
          
          {/* Mode Toggle */}
          <div className="flex rounded-lg overflow-hidden border border-zinc-700">
            <button
              onClick={() => setMode('buy')}
              className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                mode === 'buy' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              Buy STXOSHI
            </button>
            <button
              onClick={() => setMode('sell')}
              className={`flex-1 py-2 px-4 text-sm font-medium transition-colors ${
                mode === 'sell' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              Sell STXOSHI
            </button>
          </div>

          {/* Current Rate */}
          <div className="bg-zinc-800/50 rounded-lg p-3 flex items-center justify-between">
            <span className="text-sm text-zinc-400">Current Rate</span>
            <div className="flex items-center gap-2">
              {priceLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
              ) : (
                <>
                  <span className="text-sm font-medium">
                    {mode === 'buy' 
                      ? `1 STX = ${priceData?.stxoshi_per_stx?.toLocaleString() || '...'} STXOSHI`
                      : `1 STX = ${sellPriceData?.stxoshi_per_stx?.toLocaleString() || '...'} STXOSHI`
                    }
                  </span>
                  <button onClick={fetchPrice} className="text-zinc-500 hover:text-white">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Available STX for sell (liquidity) */}
          {mode === 'sell' && sellPriceData && (
            <div className="text-xs text-zinc-500 text-center">
              Available liquidity: {sellPriceData.stx_available?.toFixed(2) || 0} STX 
              (~{sellPriceData.max_stxoshi_sell?.toLocaleString() || 0} STXOSHI max)
            </div>
          )}

          {/* Swap completed state */}
          {swapStatus === 'complete' && completedSwap ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <h3 className="font-bold text-green-400 mb-2">Swap Complete!</h3>
              <p className="text-sm text-zinc-300 mb-3">
                {mode === 'buy' 
                  ? `You swapped ${completedSwap.stxAmount} STX for ${completedSwap.stxoshiAmount?.toLocaleString()} STXOSHI`
                  : `You swapped ${completedSwap.stxoshiAmount?.toLocaleString()} STXOSHI for ${completedSwap.stxAmount?.toFixed(6)} STX`
                }
              </p>
              <p className={`text-xs mb-3 ${completedSwap.autoFulfilled !== false ? 'text-green-500' : 'text-yellow-500'}`}>
                {completedSwap.autoFulfilled !== false 
                  ? 'Automatically sent to your wallet'
                  : 'Processing... will be sent shortly'
                }
              </p>
              {completedSwap.txId && (
                <a
                  href={`https://explorer.hiro.so/txid/${completedSwap.txId}?chain=mainnet`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300"
                >
                  View Transaction <ExternalLink className="w-4 h-4" />
                </a>
              )}
              <Button 
                onClick={() => { setSwapStatus('idle'); setCompletedSwap(null); setAmount(''); }} 
                className="w-full mt-4"
                variant="outline"
              >
                Make Another Swap
              </Button>
            </div>
          ) : (
            <>
              {/* Input Amount */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-zinc-400">You Send</span>
                  <span className="text-xs text-zinc-500">
                    Min: {minAmount.toLocaleString()} | Max: {maxAmount.toLocaleString()}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder={`Enter ${inputToken} amount`}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white flex-1"
                    min={minAmount}
                    max={maxAmount}
                    disabled={loading}
                  />
                  <div className="bg-zinc-800 border border-zinc-700 rounded-md px-3 flex items-center">
                    <span className="font-medium text-amber-400">{inputToken}</span>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex justify-center">
                <ArrowRight className="w-5 h-5 text-zinc-500" />
              </div>

              {/* Output Amount */}
              <div className="space-y-2">
                <span className="text-sm text-zinc-400">You Receive</span>
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-amber-400">
                      {quote ? (
                        mode === 'buy' 
                          ? quote.output.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })
                          : quote.output.amount.toFixed(6)
                      ) : '0'}
                    </span>
                    <span className="text-lg font-medium text-zinc-400">{outputToken}</span>
                  </div>
                  {quote && (
                    <p className="text-xs text-zinc-500 mt-1">
                      ≈ ${quote.output.usd_value?.toFixed(4) || '0'} USD
                    </p>
                  )}
                </div>
              </div>

              {/* Info Note */}
              <div className="flex items-start gap-2 text-xs text-zinc-500 bg-zinc-800/50 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>
                  {outputToken} tokens will be sent automatically to your wallet after your {inputToken} deposit is confirmed (~10-15 minutes).
                </p>
              </div>

              {/* Action Button */}
              <Button
                onClick={isConnected ? handleSwap : undefined}
                disabled={!isConnected || loading || !quote || !amount || parseFloat(amount) < minAmount}
                className={`w-full font-bold ${
                  mode === 'buy' 
                    ? 'bg-green-600 hover:bg-green-500 disabled:bg-green-600/50' 
                    : 'bg-red-600 hover:bg-red-500 disabled:bg-red-600/50'
                } text-white disabled:opacity-60`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {swapStatus === 'signing' ? 'Sign in Wallet...' : 'Processing...'}
                  </>
                ) : (
                  <>
                    <ArrowDownUp className="w-4 h-4 mr-2" />
                    {mode === 'buy' ? 'Buy STXOSHI' : 'Sell STXOSHI'}
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
